import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Mail, Coffee, ArrowRight, Copy, CheckCheck } from 'lucide-react';
import { Button } from '../components/ui/button';

const SuccessPage = () => {
  const navigate = useNavigate();
  const [registration, setRegistration] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const regData = localStorage.getItem('contemplate_registration');
    if (!regData) {
      navigate('/');
      return;
    }
    setRegistration(JSON.parse(regData));

    // Track successful registration
    if (window.gtag) {
      window.gtag('event', 'conversion', {
        send_to: 'AW-XXXXXXXXX/XXXXXXXXXXXX', // Replace with your conversion ID
        value: 199,
        currency: 'USD'
      });
    }
  }, [navigate]);

  const copyCode = () => {
    if (registration?.registrationCode) {
      navigator.clipboard.writeText(registration.registrationCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!registration) return null;

  return (
    <div className="min-h-screen bg-[#efede6] flex items-center justify-center px-8 py-16">
      <div className="max-w-2xl w-full text-center">
        {/* Success Animation */}
        <div className="mb-10">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-[#202020] mb-6 animate-bounce">
            <Check className="w-12 h-12 text-[#efede6]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-normal text-[#202020] mb-4 tracking-tight">
            Welcome to Contemplate, {registration.name?.split(' ')[0]}!
          </h1>
          <p className="text-[#202020]/60 text-xl">
            Your journey to peace begins now.
          </p>
        </div>

        {/* Registration Code Card */}
        <div className="bg-white rounded-3xl p-10 shadow-xl mb-10">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Mail className="w-5 h-5 text-[#202020]/60" />
            <span className="text-[#202020]/60">Check your email</span>
          </div>
          
          <p className="text-[#202020]/70 mb-6">
            We've sent your program access details to <strong>{registration.email}</strong>
          </p>

          <div className="bg-[#efede6] rounded-2xl p-6 mb-6">
            <p className="text-[#202020]/50 text-sm mb-2">Your Registration Code</p>
            <div className="flex items-center justify-center gap-3">
              <span className="text-3xl font-mono font-bold text-[#202020] tracking-wider">
                {registration.registrationCode}
              </span>
              <button
                onClick={copyCode}
                className="p-2 rounded-lg hover:bg-[#202020]/10 transition-colors"
                title="Copy code"
              >
                {copied ? (
                  <CheckCheck className="w-5 h-5 text-green-600" />
                ) : (
                  <Copy className="w-5 h-5 text-[#202020]/40" />
                )}
              </button>
            </div>
          </div>

          <p className="text-[#202020]/50 text-sm">
            Use this code to access your Contemplate program. Keep it safe!
          </p>
        </div>

        {/* Instructions */}
        <div className="bg-[#202020] rounded-3xl p-10 mb-10">
          <h2 className="text-2xl font-normal text-[#efede6] mb-6">How to use your code:</h2>
          
          <div className="space-y-6 text-left">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-[#efede6]/10 flex items-center justify-center flex-shrink-0 text-[#efede6] font-medium">1</div>
              <div>
                <p className="text-[#efede6] font-medium">Register on stage.contemplate.life</p>
                <p className="text-[#efede6]/50 text-sm">Create your account on the Contemplate platform</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-[#efede6]/10 flex items-center justify-center flex-shrink-0 text-[#efede6] font-medium">2</div>
              <div>
                <p className="text-[#efede6] font-medium">Click on "Join using a Code" button</p>
                <p className="text-[#efede6]/50 text-sm">Once registered, find and click the code entry option</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-[#efede6]/10 flex items-center justify-center flex-shrink-0 text-[#efede6] font-medium">3</div>
              <div>
                <p className="text-[#efede6] font-medium">Enter your code and submit</p>
                <p className="text-[#efede6]/50 text-sm">Use the code above: <strong className="text-[#efede6]">{registration.registrationCode}</strong></p>
              </div>
            </div>
            
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-[#efede6]/10 flex items-center justify-center flex-shrink-0 text-[#efede6] font-medium">4</div>
              <div>
                <p className="text-[#efede6] font-medium">Get access to the program</p>
                <p className="text-[#efede6]/50 text-sm">Start your journey to peaceful parenting</p>
              </div>
            </div>
          </div>
        </div>

        {/* Encouragement */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <Coffee className="w-6 h-6 text-[#202020]/40" />
          <p className="text-[#202020]/60 italic">
            "The journey of a thousand miles begins with a single sip." 
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={() => window.location.href = 'https://stage.contemplate.life'}
            className="bg-[#202020] hover:bg-[#303030] text-[#efede6] px-8 py-6 rounded-full text-base font-medium transition-all hover:scale-105 group"
          >
            Go to stage.contemplate.life
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
          
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="border-[#202020]/20 text-[#202020] hover:bg-[#202020]/5 px-8 py-6 rounded-full text-base"
          >
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;
